import React, { forwardRef } from 'react';
import {
	GestureResponderEvent,
	Pressable as RNPressable,
	PressableProps,
	TouchableOpacity as RNTouchableOpacity,
	TouchableOpacityProps,
} from 'react-native';

import { playSound, SoundName } from '../../../utils/sound';

/**
 * Drop-in replacements for react-native's TouchableOpacity and Pressable that play
 * the button click on press.
 *
 * v1 did this with one global click hook: every element carrying
 * `play-button-click` (140 of them across header, footer, profile, ranking, shop
 * and every modal) played ButtonPressed. React Native has no equivalent hook, so
 * the app imports these wrappers instead of the bare primitives (see the
 * `Components/common/Touchable` imports across src/app) and gets the same coverage.
 *
 * - `sound={false}` opts out, for controls that play their own effect (the sound
 *   toggle plays ToggleSound, even when sound is off, as feedback).
 * - `sound='ToggleSound'` etc. swaps the effect (RenderToggle).
 * - A touchable with no onPress is a container, not a button: nothing plays.
 * - The click respects the user's "sound effects" setting (playSound does).
 */
type SoundProps = { sound?: SoundName | false };

const withClick = <E extends GestureResponderEvent>(
	sound: SoundName | false,
	onPress: ((event: E) => void) | null | undefined,
) =>
	onPress
		? (event: E) => {
				if (sound) playSound(sound);
				onPress(event);
			}
		: undefined;

export const TouchableOpacity = forwardRef<
	React.ComponentRef<typeof RNTouchableOpacity>,
	TouchableOpacityProps & SoundProps
>(({ sound = 'ButtonPressed', onPress, ...rest }, ref) => (
	<RNTouchableOpacity ref={ref} {...rest} onPress={withClick(sound, onPress)} />
));
TouchableOpacity.displayName = 'TouchableOpacity';

export const Pressable = forwardRef<
	React.ComponentRef<typeof RNPressable>,
	PressableProps & SoundProps
>(({ sound = 'ButtonPressed', onPress, ...rest }, ref) => (
	<RNPressable ref={ref} {...rest} onPress={withClick(sound, onPress)} />
));
Pressable.displayName = 'Pressable';
